"use client"
import {
  Button,
  Box,
  Heading,
  Text,
  Badge,
  useToast,
  Container,
  VStack,
  HStack,
  SimpleGrid,
  useColorModeValue,
  Alert,
  AlertIcon,
} from "@chakra-ui/react"
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  BuildingOfficeIcon,
  BriefcaseIcon,
  MapPinIcon,
  CalendarIcon,
  UserGroupIcon,
  DocumentTextIcon,
  AcademicCapIcon,
  ClockIcon,
  PhoneIcon,
  CheckCircleIcon,
  CurrencyRupeeIcon,
} from "@heroicons/react/24/outline"
import { useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import { createJobAsync } from "../Employers Slice/employersJobCreateSlice/createJobSlice"

export function JobReview() {
  const dispatch = useDispatch()
  const location = useLocation().state
  const navigate = useNavigate()
  const toast = useToast()
  const accentColor = useColorModeValue("blue.500", "blue.300")
  const formBackground = useColorModeValue("white", "gray.700")
  const sectionBgColor = useColorModeValue("blue.50", "blue.900")
  const borderColor = useColorModeValue("blue.100", "blue.700")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState(null)

  const toNumber = (v) => (v === "" || v == null ? undefined : Number(v))
  const toIdList = (list) =>
    Array.isArray(list) ? list.map((x) => x.id ?? x.uuid ?? x).filter(Boolean) : []
  const parseBenefits = (str) =>
    Array.from(new Set((str || "").split(",").map((s) => s.trim()).filter(Boolean)))

  const safeParse = (value) => {
    if (!value) return null
    if (typeof value === "string") {
      try {
        return JSON.parse(value)
      } catch {
        return null
      }
    }
    if (typeof value === "object") return value
    return null
  }

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true)
      setError(null)

      if (!location?.job_name || !location?.job_description) {
        throw new Error("Job name and description are required fields")
      }

      const isNotDisclosed =
        !!location.salary_not_disclose || location.pay_type === "Do Not Disclose"
      const normalizedPayType =
        location.pay_type || (isNotDisclosed ? "Do Not Disclose" : "Salary Range")
      const isExactAmount = !isNotDisclosed && normalizedPayType === "Exact Amount"

      const newData = {
        additional_pay: parseBenefits(location.additional_pay).join(", "),
        address1: location.address1,
        address2: location.address2,
        city_uuid: safeParse(location.city_uuid)?.uuid,
        country_uuid: safeParse(location.country_uuid)?.uuid,
        department_uuid: safeParse(location.department_uuid)?.uuid,
        hiring_company_name: location.hiring_company_name,
        job_name: location.job_name,
        job_apply_age: location.job_apply_age,
        is_planned_start_date: !!location.is_planned_start_date,
        job_description: location.job_description,
        job_shift: location.job_shift,
        job_tag: location.job_tag,
        job_title_uuid: safeParse(location.job_title_uuid)?.uuid,
        job_type: location.job_type,
        joining_amount_required: !!location.joining_amount_required,
        joining_amount: toNumber(location.joining_amount),
        no_of_hire: toNumber(location.no_of_hire),
        pincode: toNumber(location.pincode),
        plan_start_date: location.plan_start_date || null,

        // Salary handling (supports Not Disclosed, Exact Amount, and Range)
        salary_not_disclose: isNotDisclosed,
        pay_type: normalizedPayType,
        salary_exact_amount: isExactAmount ? toNumber(location.salary_exact_amount) : undefined,
        salary_min_amount:
          !isNotDisclosed && !isExactAmount ? toNumber(location.salary_min_amount) : undefined,
        salary_max_amount:
          !isNotDisclosed && !isExactAmount ? toNumber(location.salary_max_amount) : undefined,
        salary_per_type: isNotDisclosed ? undefined : location.salary_per_type,

        state_uuid: safeParse(location.state_uuid)?.uuid,
        work_location_type: location.work_location_type,

        candidate_contact_via: location.candidate_contact_via,
        interview_type: location.interview_type,
        job_deadline: location.job_deadline,
        candidate_gender: location.candidate_gender,

        // IDs only for arrays (Education array contains "last selected" UUIDs)
        job_location: toIdList(location.job_location),
        job_education: toIdList(location.job_education),
        job_skills: toIdList(location.job_skills),

        min_job_exp: toNumber(location.min_job_exp),
        max_job_exp: toNumber(location.max_job_exp),

        upload_cv: !!location.upload_cv,
        is_published: !!location.is_published,
        urgent_hiring_tag_enable: false,
        status: location.status || "Active",
      }

      await dispatch(createJobAsync({ data: newData })).unwrap()

      toast({
        title: "Job Posted Successfully!",
        duration: 3000,
        isClosable: true,
        position: "top",
        status: "success",
      })
      navigate("/employer-dashboard/manage-jobs")
    } catch (err) {
      const message = err?.message || err?.data?.message || "An error occurred while posting the job"
      setError(message)
      toast({
        title: "Error",
        description: message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // Helper to show comma-separated titles regardless of item shape
  const formatSkills = (items) => {
    if (!items || !Array.isArray(items) || items.length === 0) {
      return "None specified"
    }
    return items
      .map((item) => (typeof item === "string" ? item : item?.title))
      .filter(Boolean)
      .join(", ")
  }

  const formatBoolean = (value) => {
    return value ? "Yes" : "No"
  }

  const getJobTypeBadgeColor = (jobType) => {
    switch (jobType) {
      case "Full Time":
        return "green"
      case "Part Time":
        return "purple"
      case "Internship":
        return "orange"
      case "Freelance":
        return "blue"
      case "Temporary":
        return "red"
      default:
        return "gray"
    }
  }

  const getLocationTypeBadgeColor = (locationType) => {
    switch (locationType) {
      case "On Site":
        return "blue"
      case "Remote":
        return "green"
      case "Hybrid":
        return "purple"
      default:
        return "gray"
    }
  }

  const containsHTML = (str) => {
    if (!str) return false
    return /<[a-z][\s\S]*>/i.test(str)
  }

  const richTextStyles = {
    "& .job-description-content": {
      lineHeight: "1.6",
      color: "inherit",
    },
    "& .job-description-content p": {
      marginBottom: "1rem",
      lineHeight: "1.6",
    },
    "& .job-description-content ul, & .job-description-content ol": {
      paddingLeft: "1.5rem",
      marginBottom: "1rem",
      lineHeight: "1.6",
    },
    "& .job-description-content ul": {
      listStyleType: "disc",
    },
    "& .job-description-content ol": {
      listStyleType: "decimal",
    },
    "& .job-description-content li": {
      marginBottom: "0.5rem",
      lineHeight: "1.6",
    },
    "& .job-description-content h1, & .job-description-content h2, & .job-description-content h3, & .job-description-content h4, & .job-description-content h5, & .job-description-content h6": {
      fontWeight: "bold",
      marginTop: "1.5rem",
      marginBottom: "1rem",
      lineHeight: "1.4",
    },
    "& .job-description-content h1": {
      fontSize: "1.875rem",
      fontWeight: "800",
    },
    "& .job-description-content h2": {
      fontSize: "1.5rem",
      fontWeight: "700",
    },
    "& .job-description-content h3": {
      fontSize: "1.25rem",
      fontWeight: "600",
    },
    "& .job-description-content blockquote": {
      borderLeft: "4px solid #e2e8f0",
      paddingLeft: "1rem",
      fontStyle: "italic",
      margin: "1rem 0",
      backgroundColor: "#f7fafc",
      padding: "1rem",
      borderRadius: "0.375rem",
    },
    "& .job-description-content a": {
      color: "#3182ce",
      textDecoration: "underline",
    },
    "& .job-description-content a:hover": {
      color: "#2c5aa0",
    },
    "& .job-description-content strong, & .job-description-content b": {
      fontWeight: "bold",
    },
    "& .job-description-content em, & .job-description-content i": {
      fontStyle: "italic",
    },
    "& .job-description-content u": {
      textDecoration: "underline",
    },
    "& .job-description-content strike": {
      textDecoration: "line-through",
    },
    "& .job-description-content code": {
      backgroundColor: "#f1f5f9",
      padding: "0.125rem 0.25rem",
      borderRadius: "0.25rem",
      fontSize: "0.875rem",
      fontFamily: "monospace",
    },
    "& .job-description-content pre": {
      backgroundColor: "#f1f5f9",
      padding: "1rem",
      borderRadius: "0.375rem",
      overflow: "auto",
      marginBottom: "1rem",
    },
  }

  if (!location) {
    return (
      <Box minH="100vh" py={12} className=" bg-mainBg">
        <Container maxW="6xl">
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            No job data found. Please complete the job posting form.
          </Alert>
        </Container>
      </Box>
    )
  }

  // For display only: determine salary presentation
  const reviewNotDisclosed =
    !!location.salary_not_disclose || location.pay_type === "Do Not Disclose"
  const isExactAmountDisplay =
    !reviewNotDisclosed && (location.pay_type === "Exact Amount")

  const dept = safeParse(location.department_uuid)
  const jobTitle = safeParse(location.job_title_uuid)
  const city = safeParse(location.city_uuid)
  const state = safeParse(location.state_uuid)
  const country = safeParse(location.country_uuid)

  const experienceDisplay =
    Number(location?.min_job_exp) === 0 && Number(location?.max_job_exp) === 0
      ? "Fresher"
      : `${location.min_job_exp} - ${location.max_job_exp} years`

  return (
    <Box minH="100vh" py={12} className=" bg-mainBg">
      <Container maxW="6xl">
        <Box bg={formBackground} borderRadius="xl" shadow="xl" p={6} transition="all 0.3s">
          <HStack spacing={3} mb={8} justify="center">
            <Heading as="h2" size="xl" textAlign="center" color={accentColor}>
              Review Your Job Posting
            </Heading>
          </HStack>

          <Alert status="info" mb={6} borderRadius="md">
            <AlertIcon />
            Please review all details carefully before submitting your job posting.
          </Alert>

          {error && (
            <Alert status="error" mb={6} borderRadius="md">
              <AlertIcon />
              {error}
            </Alert>
          )}

          <VStack spacing={6} align="stretch">
            <Box p={4} borderLeft="4px solid" borderColor={accentColor} bg={sectionBgColor} borderRadius="md">
              <Heading as="h3" size="md" mb={2}>
                Basic Job Information
              </Heading>
              <Text color="gray.600">Review the fundamental details about the position.</Text>
            </Box>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <Box p={4} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
                <HStack mb={2} className="" alignItems={"start"}>
                  <BuildingOfficeIcon className="h-5 w-5 text-blue-500" />
                  <Text fontWeight="semibold">Company</Text>
                </HStack>
                <Text>{location.hiring_company_name}</Text>
              </Box>

              <Box p={4} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
                <HStack mb={2} alignItems={"start"}>
                  <BriefcaseIcon className="h-5 w-5 text-blue-500" />
                  <Text fontWeight="semibold">Job Title</Text>
                </HStack>
                <Text>{jobTitle?.title || "-"}</Text>
              </Box>

              <Box p={4} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
                <HStack mb={2} alignItems={"start"}>
                  <BriefcaseIcon className="h-5 w-5 text-blue-500" />
                  <Text fontWeight="semibold">Designation</Text>
                </HStack>
                <Text>{location.job_name}</Text>
              </Box>

              <Box p={4} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
                <HStack mb={2} alignItems={"start"}>
                  <BriefcaseIcon className="h-5 w-5 text-blue-500" />
                  <Text fontWeight="semibold">Department</Text>
                </HStack>
                <Text>{dept?.title || "-"}</Text>
              </Box>

              <Box p={4} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
                <HStack mb={2} alignItems={"start"}>
                  <BriefcaseIcon className="h-5 w-5 text-blue-500" />
                  <Text fontWeight="semibold">Job Type</Text>
                </HStack>
                <Badge colorScheme={getJobTypeBadgeColor(location.job_type)} px={2} py={1} borderRadius="full">
                  {location.job_type}
                </Badge>
              </Box>

              <Box p={4} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
                <HStack mb={2} alignItems={"start"}>
                  <MapPinIcon className="h-5 w-5 text-blue-500" />
                  <Text fontWeight="semibold">Location Type</Text>
                </HStack>
                <Badge
                  colorScheme={getLocationTypeBadgeColor(location.work_location_type)}
                  px={2}
                  py={1}
                  borderRadius="full"
                >
                  {location.work_location_type}
                </Badge>
              </Box>

              <Box p={4} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
                <HStack mb={2} alignItems={"start"}>
                  <ClockIcon className="h-5 w-5 text-blue-500" />
                  <Text fontWeight="semibold">Shift</Text>
                </HStack>
                <Text>{location.job_shift}</Text>
              </Box>

              <Box p={4} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
                <HStack mb={2} alignItems={"start"}>
                  <UserGroupIcon className="h-5 w-5 text-blue-500" />
                  <Text fontWeight="semibold">Number of Openings</Text>
                </HStack>
                <Text>{location.no_of_hire || "Not specified"}</Text>
              </Box>
            </SimpleGrid>

            <Box p={4} borderLeft="4px solid" borderColor={accentColor} bg={sectionBgColor} borderRadius="md" mt={4}>
              <Heading as="h3" size="md" mb={2}>
                Job Location
              </Heading>
              <Text color="gray.600">Review the address details for this position.</Text>
            </Box>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <Box p={4} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
                <HStack mb={2} alignItems={"start"}>
                  <MapPinIcon className="h-5 w-5 text-blue-500" />
                  <Text fontWeight="semibold">Address</Text>
                </HStack>
                <Text>{location.address1}</Text>
                {location.address2 && <Text mt={1}>{location.address2}</Text>}
              </Box>

              <Box p={4} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
                <HStack mb={2} alignItems={"start"}>
                  <MapPinIcon className="h-5 w-5 text-blue-500" />
                  <Text fontWeight="semibold">Location</Text>
                </HStack>
                <Text>
                  {(city?.title || "-")}, {(state?.title || "-")}, {(country?.title || "-")}
                </Text>
              </Box>

              <Box p={4} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
                <HStack mb={2} alignItems={"start"}>
                  <MapPinIcon className="h-5 w-5 text-blue-500" />
                  <Text fontWeight="semibold">Postal Code</Text>
                </HStack>
                <Text>{location.pincode}</Text>
              </Box>

              <Box p={4} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
                <HStack mb={2} alignItems={"start"}>
                  <MapPinIcon className="h-5 w-5 text-blue-500" />
                  <Text fontWeight="semibold">Job Posting Locations</Text>
                </HStack>
                <Text>{formatSkills(location.job_location)}</Text>
              </Box>
            </SimpleGrid>

            <Box p={4} borderLeft="4px solid" borderColor={accentColor} bg={sectionBgColor} borderRadius="md" mt={4}>
              <Heading as="h3" size="md" mb={2}>
                Salary & Benefits
              </Heading>
              <Text color="gray.600">Review salary and benefits information.</Text>
            </Box>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <Box p={4} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
                <HStack mb={2} alignItems={"start"}>
                  <CurrencyRupeeIcon className="h-5 w-5 text-blue-500" />
                  <Text fontWeight="semibold">Salary</Text>
                </HStack>
                {reviewNotDisclosed ? (
                  <Text>Not disclosed</Text>
                ) : isExactAmountDisplay ? (
                  <Text>
                    {location.salary_exact_amount} ({location.salary_per_type})
                  </Text>
                ) : (
                  <Text>
                    {location.salary_min_amount} - {location.salary_max_amount} ({location.salary_per_type})
                  </Text>
                )}
              </Box>

              <Box p={4} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
                <HStack mb={2} alignItems={"start"}>
                  <CurrencyRupeeIcon className="h-5 w-5 text-blue-500" />
                  <Text fontWeight="semibold">Joining Fee Required</Text>
                </HStack>
                <Badge
                  colorScheme={location.joining_amount_required ? "red" : "green"}
                  px={2}
                  py={1}
                  borderRadius="full"
                >
                  {formatBoolean(location.joining_amount_required)}
                </Badge>
                <Text mt={1}>
                  {location.joining_amount_required ? location.joining_amount : "—"}
                </Text>
              </Box>

              {location.additional_pay && (
                <Box p={4} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
                  <HStack mb={2} alignItems={"start"}>
                    <CurrencyRupeeIcon className="h-5 w-5 text-blue-500" />
                    <Text fontWeight="semibold">Additional Benefits</Text>
                  </HStack>
                  <Text>{location.additional_pay}</Text>
                </Box>
              )}
            </SimpleGrid>

            <Box p={4} borderLeft="4px solid" borderColor={accentColor} bg={sectionBgColor} borderRadius="md" mt={4}>
              <Heading as="h3" size="md" mb={2}>
                Job Description
              </Heading>
              <Text color="gray.600">Review the job description and requirements.</Text>
            </Box>

            <Box p={4} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
              <HStack mb={2} alignItems={"start"}>
                <DocumentTextIcon className="h-5 w-5 text-blue-500" />
                <Text fontWeight="semibold">Job Description</Text>
              </HStack>
              <Box p={4} bg="gray.50" borderRadius="md" mt={2} sx={richTextStyles} className="job-description-container">
                {location.job_description && containsHTML(location.job_description) ? (
                  <Box
                    dangerouslySetInnerHTML={{ __html: location.job_description }}
                    className="job-description-content"
                  />
                ) : (
                  <Text whiteSpace="pre-wrap" className="job-description-content">
                    {location.job_description || "No job description provided"}
                  </Text>
                )}
              </Box>
            </Box>

            <Box p={4} borderLeft="4px solid" borderColor={accentColor} bg={sectionBgColor} borderRadius="md" mt={4}>
              <Heading as="h3" size="md" mb={2}>
                Skills and Qualifications
              </Heading>
              <Text color="gray.600">Review the required skills and qualifications.</Text>
            </Box>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <Box p={4} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
                <HStack mb={2} alignItems={"start"}>
                  <AcademicCapIcon className="h-5 w-5 text-blue-500" />
                  <Text fontWeight="semibold">Required Skills</Text>
                </HStack>
                <Text>{formatSkills(location.job_skills)}</Text>
              </Box>

              <Box p={4} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
                <HStack mb={2} alignItems={"start"}>
                  <AcademicCapIcon className="h-5 w-5 text-blue-500" />
                  <Text fontWeight="semibold">Education Requirements</Text>
                </HStack>
                {/* Titles already hold the chain: Level / Course / Speciality */}
                <Text>{formatSkills(location.job_education)}</Text>
              </Box>

              <Box p={4} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
                <HStack mb={2} alignItems={"start"}>
                  <BriefcaseIcon className="h-5 w-5 text-blue-500" />
                  <Text fontWeight="semibold">Experience Required</Text>
                </HStack>
                <Text>{experienceDisplay}</Text>
              </Box>

              <Box p={4} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
                <HStack mb={2} alignItems={"start"}>
                  <UserGroupIcon className="h-5 w-5 text-blue-500" />
                  <Text fontWeight="semibold">Age Range</Text>
                </HStack>
                <Text>{location.job_apply_age}</Text>
              </Box>

              <Box p={4} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
                <HStack mb={2} alignItems={"start"}>
                  <UserGroupIcon className="h-5 w-5 text-blue-500" />
                  <Text fontWeight="semibold">Gender</Text>
                </HStack>
                <Text>{location.candidate_gender}</Text>
              </Box>
            </SimpleGrid>

            <Box p={4} borderLeft="4px solid" borderColor={accentColor} bg={sectionBgColor} borderRadius="md" mt={4}>
              <Heading as="h3" size="md" mb={2}>
                Application Process
              </Heading>
              <Text color="gray.600">Review the application and interview details.</Text>
            </Box>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <Box p={4} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
                <HStack mb={2} alignItems={"start"}>
                  <CalendarIcon className="h-5 w-5 text-blue-500" />
                  <Text fontWeight="semibold">Application Deadline</Text>
                </HStack>
                <Text>{location.job_deadline}</Text>
              </Box>

              {!!location.is_planned_start_date && (
                <Box p={4} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
                  <HStack mb={2} alignItems={"start"}>
                    <CalendarIcon className="h-5 w-5 text-blue-500" />
                    <Text fontWeight="semibold">Planned Start Date</Text>
                  </HStack>
                  <Text>{location.plan_start_date}</Text>
                </Box>
              )}

              <Box p={4} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
                <HStack mb={2} alignItems={"start"}>
                  <BriefcaseIcon className="h-5 w-5 text-blue-500" />
                  <Text fontWeight="semibold">Interview Type</Text>
                </HStack>
                <Badge
                  colorScheme={location.interview_type === "In-Person" ? "orange" : "blue"}
                  px={2}
                  py={1}
                  borderRadius="full"
                >
                  {location.interview_type}
                </Badge>
              </Box>

              <Box p={4} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
                <HStack mb={2} alignItems={"start"}>
                  <PhoneIcon className="h-5 w-5 text-blue-500" />
                  <Text fontWeight="semibold">Contact Preference</Text>
                </HStack>
                <Text>{location.candidate_contact_via}</Text>
              </Box>

              <Box p={4} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
                <HStack mb={2} alignItems={"start"}>
                  <DocumentTextIcon className="h-5 w-5 text-blue-500" />
                  <Text fontWeight="semibold">CV Required</Text>
                </HStack>
                <Badge colorScheme={location.upload_cv ? "green" : "red"} px={2} py={1} borderRadius="full">
                  {formatBoolean(location.upload_cv)}
                </Badge>
              </Box>
            </SimpleGrid>

            <Box p={4} borderWidth="1px" borderRadius="md" borderColor={borderColor} mt={4} bg="gray.50">
              <HStack mb={2} align="flex-start">
                <CheckCircleIcon className="h-5 w-5 text-blue-500 mt-1" />
                <Text fontSize="sm" color="gray.600">
                  By selecting Confirm, you agree that this job post reflects your requirements, and agree it will be
                  posted and applications will be processed following our Terms, Cookie, and Privacy Policies.
                </Text>
              </HStack>
            </Box>

            <HStack justifyContent="space-between" pt={6}>
              <Button
                leftIcon={<ChevronLeftIcon className="h-5 w-5" />}
                colorScheme="blue"
                variant="outline"
                size="lg"
                _hover={{ bg: "blue.50" }}
                onClick={() => navigate("/employer-dashboard/post-job/additional", { state: location })}
                isDisabled={isSubmitting}
              >
                Back
              </Button>

              <Button
                rightIcon={!isSubmitting && <ChevronRightIcon className="h-5 w-5" />}
                colorScheme="blue"
                size="lg"
                px={8}
                _hover={{ transform: "translateY(-2px)", boxShadow: "lg" }}
                transition="all 0.2s"
                onClick={handleSubmit}
                isLoading={isSubmitting}
                loadingText="Submitting..."
                isDisabled={isSubmitting}
              >
                {isSubmitting ? "Posting Job..." : "Confirm & Post Job"}
              </Button>
            </HStack>
          </VStack>
        </Box>
      </Container>
    </Box>
  )
}

export default JobReview